import { dispatchAction } from '@ardoq/rxbeach';
import {
  SetHighlightArgs,
  layoutComplete,
  relationshipsViewLayoutUpdated,
  relationshipsViewSetHighlight,
  relationshipsViewUpdateWindowRect,
  relationshipsViewZoomToFit,
  zoomChanged,
} from './actions';
import {
  LayoutType,
  RelationshipsLink,
  RelationshipsLinkVisual,
  RelationshipsNode,
  RelationshipsViewCommands,
  RelationshipsViewSettings,
  VisualStatesBase,
  VisualStatesData,
  WindowRect,
} from './types';
import { enrichToVisualLinks } from './controllerUtils';
import * as React from 'react';
import { EXPANDER_RADIUS, NODE_RADIUS } from './consts';
import { ZoomTransform, zoomIdentity } from 'd3';
import {
  areaOfCircle,
  radiusOfCircle,
  radiusScaler,
  getComponentLabelParts,
} from '@ardoq/graph';
import { geometry } from '@ardoq/math';
import { HitContext } from './HitContext';
import {
  clearSubscriptions,
  onFirstAction,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import { collapseLinks } from './collapseLinks';
import { isAllCollapsed, isTruthy, returnZero } from '@ardoq/common-helpers';
import { isEqual, throttle } from 'lodash';
import {
  ancestorsForEach,
  ancestorsIncludes,
  descendantsForEach,
  findDescendant,
  includeAllDescendants,
} from './util';
import {
  absolutePosition,
  drawNode,
  getChildCountBadgeFontSizeForNode,
  getNodeFontSize,
  getSubLabelHeight,
  isChildCountBadgeToBeDisplayed,
  shouldShowNodeLabel,
} from './visuals/node';
import { addLinkToContext, drawLink, getLinkPoints } from './visuals/link';
import {
  canvasResolvedImageReady,
  svgImageReady,
} from 'tabview/canvasRendering/canvasResolvedImages';
import { createDispatcher } from './view/createDispatcher';
import { groupDisconnectedChildren } from './viewModel$/groupDisconnectedChildren';
import { layoutNodeChildren } from './layout/layoutNodeChildren';
import { getMinAndMaxDescendants } from './layout/getMinAndMaxDescendants';
import { truncateLabel, clearFontMetricsCache } from './layout/truncateLabel';
import { applyPreviousPositions } from './layout/applyPreviousPositions';
import { getGroupNodesWithChangedOpenState } from './getGroupNodesWithChangedOpenState';
import {
  applyCollapsedGroupIds,
  getGroupOpenDecision,
} from './applyCollapsedGroupIds';
import { currentTimestamp } from '@ardoq/date-time';
import { nodeLabelY } from './visuals/nodeLabelY';
import { badgeLocation, shouldShowExpander } from './visuals/util';
import { descendantCountBadgeTextAndIconMetrics } from 'tabview/canvasRendering/descendantCountBadge/descendantCountBadgeTextAndIconMetrics';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { startViewWork } from 'tabview/activeViewsStatus/viewStatusReporter';

const isInsideLegacy = (node: RelationshipsNode, window: WindowRect) => {
  const [windowLeft, windowTop, windowRight, windowBottom] = window;
  const nodeLeft = node.x - node.r;
  const nodeRight = node.x + node.r;
  const nodeTop = node.y - node.r;
  const nodeBottom = node.y + node.r;
  return (
    nodeLeft >= windowLeft &&
    nodeRight <= windowRight &&
    nodeTop >= windowTop &&
    nodeBottom <= windowBottom
  );
};

// Component names should be shown in a popover only when they are truncated, NOT when the names are not rendered in a zoomed out state.
// Group names should be shown in a popover only when they are truncated, OR when the names are not rendered in a zoomed out state.
// https://ardoqcom.atlassian.net/browse/ARD-11738
/**
 * checks if the popover should be displayed.
 *
 * @returns true if the node label was truncated and (large enough to print on-screen or is a group label).
 */
const shouldShowPopover = (
  node: RelationshipsNode,
  viewTransform: ZoomTransform,
  scale: number,
  truncatedLabel?: string
) =>
  truncatedLabel !== node.label &&
  (node.descendantCount ||
    shouldShowNodeLabel(getNodeFontSize(node, viewTransform, scale)));

/** minimum group node radius */
const RADIUS_MIN = 2 * NODE_RADIUS;
const AREA_MIN = areaOfCircle(RADIUS_MIN);
/** maximum group node radius */
const RADIUS_MAX = 10 * RADIUS_MIN;
const TIME_MAX = 100;

const isHighlightEqual = (a: SetHighlightArgs, b: SetHighlightArgs) =>
  a.highlightedNode?.id === b.highlightedNode?.id &&
  isEqual(a.highlightedLink?.modelIds, b.highlightedLink?.modelIds);

const DIM_ALPHA = 0.28;

const clearContext = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number
) => context.clearRect(0, 0, width, height);

const fixCanvasDpi = (
  canvas: HTMLCanvasElement,
  width = canvas.offsetWidth * devicePixelRatio,
  height = canvas.offsetHeight * devicePixelRatio
) => {
  canvas.setAttribute('width', `${width}`);
  canvas.setAttribute('height', `${height}`);
};

/** this is stupid */
const isAcceptableEntropy = (entropy: number, elapsedMilliseconds: number) =>
  entropy > 20
    ? elapsedMilliseconds > 10000
    : entropy > 1
      ? elapsedMilliseconds > 5000
      : entropy < 0.01 || elapsedMilliseconds > 2000;

const isGroupNode = ({ isSynthetic, children }: RelationshipsNode) =>
  !isSynthetic && children?.length;

type VisualStates = VisualStatesBase & {
  highlightedLinks: RelationshipsLinkVisual[] | null;
  selectedLinks: RelationshipsLinkVisual[] | null;
};

export type RelationshipsPopover = {
  point: { x: number; y: number };
  text: string;
};
type SetRelationshipsPopover = (popover: RelationshipsPopover | null) => void;

type LayoutArgs = {
  canvas: HTMLCanvasElement;
  rootNode: RelationshipsNode;
  links: RelationshipsLink[];
  viewSettings: RelationshipsViewSettings;
  viewInstanceId: string;
  isViewpointMode: boolean;
  setPopover: SetRelationshipsPopover;
  onLayoutBegin: (layout: LayoutType) => void;
  commands: RelationshipsViewCommands;
  viewStatusReporter: ViewStatusReporter;
};
export const createLayout = (createLayoutArgs: LayoutArgs) => {
  let {
    canvas,
    links: originalLinks,
    viewInstanceId,
    commands,
    viewStatusReporter,
  } = createLayoutArgs;
  const { isViewpointMode, setPopover, onLayoutBegin } = createLayoutArgs;

  const initialSettings = createLayoutArgs.viewSettings;

  // #region stateful variables. some of these will be faked out until initialize is called.
  let rootNode: RelationshipsNode | null = null;
  let links: RelationshipsLinkVisual[] = [];
  let canvasBounds: Pick<DOMRect, 'x' | 'y' | 'width' | 'height'> = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };
  let scale = 1 / devicePixelRatio; // world window scale
  let window: WindowRect | null = null; // world clip window (null means don't clip)
  let ticking = false;
  let lastScheduled = currentTimestamp();
  let dead = false;
  let layoutToken: string | null = null;
  let currentLayout: LayoutType | null = null;
  let nextLayout: LayoutType = 'packLayout';
  let isGraphFullyInView = true;
  let context: CanvasRenderingContext2D = new HitContext(); // just faking this out for the moment. it will be initialized truly when initialize is called.
  let minDescendants = 0;
  let maxDescendants = 0;
  let lastClickedNode: RelationshipsNode | null = null;
  let lastHoveredNode: RelationshipsNode | null = null;
  let lastClickedLink: RelationshipsLinkVisual | null = null;
  let groupNodeRadius: (value: number) => number = returnZero;

  const visualStates: VisualStates = {
    highlightedNodes: null,
    selectedNodes: null,
    highlightedLinks: null,
    selectedLinks: null,
  };
  // #endregion

  // #region WIP - make view settings stream (great again) the source of truth for view settings
  let viewSettings: RelationshipsViewSettings = initialSettings;
  // #endregion

  const getNodeRadius = ({ descendantCount }: RelationshipsNode) =>
    !descendantCount ? NODE_RADIUS : groupNodeRadius(descendantCount);

  const reportLayoutFinished = () => {
    if (layoutToken) {
      viewStatusReporter.reportWorkFinished(layoutToken);
      layoutToken = null;
    }
  };

  const generateLinkVisuals = () =>
    Array.from(originalLinks.reduce(collapseLinks, new Map()).values());

  const tick = () => {
    if (dead) {
      reportLayoutFinished();
      return;
    }

    const then = currentTimestamp();

    for (const node of scheduled) {
      const {
        entropy,
        minimumEnclosingCircle,
        minimumEnclosingCircleForChildren,
      } = layoutNodeChildren(nextLayout, node, getNodeRadius);

      if (minimumEnclosingCircleForChildren) {
        node.minimumEnclosingCircleForChildren =
          minimumEnclosingCircleForChildren;
      }

      const roundedRadius = Math.round(minimumEnclosingCircle.r);
      if (node.r !== roundedRadius) {
        node.r = roundedRadius;

        if (node.parent) {
          schedule(node.parent, false); // parent needs more work
        }
      }

      if (isAcceptableEntropy(entropy, currentTimestamp() - lastScheduled)) {
        deschedule(node, false); // done with this node
      }

      const now = currentTimestamp();

      if (now - then > TIME_MAX) {
        break;
      }
    }

    ticking = scheduled.size !== 0;
    if (scheduled.size === 0) {
      nextLayout = 'forceLayout';
    }
    redraw();

    if (ticking) {
      requestAnimationFrame(tick);
    } else {
      dispatchAction(
        layoutComplete({ needsFit: isGraphFullyInView }),
        viewInstanceId
      );
      reportLayoutFinished();
    }

    notifyUpdateLayout();
  };

  let focusNodes = new Set<RelationshipsNode>();
  let markedNodes = new Set<RelationshipsNode>();
  let focusLinks = new Set<RelationshipsLinkVisual>();
  let markedLinks = new Set<RelationshipsLinkVisual>();

  // #region schedule and deschedule
  const scheduled = new Set<RelationshipsNode>();
  const schedule = (node: RelationshipsNode, recursive: boolean) => {
    if (recursive) {
      [...includeAllDescendants(node)]
        .filter(({ children, open }) => children && open)
        .forEach(openDescendantGroup => scheduled.add(openDescendantGroup));
    } else {
      scheduled.add(node);
    }

    if (!ticking) {
      ticking = true;
      layoutToken = startViewWork(viewStatusReporter, 'layout');
      lastScheduled = currentTimestamp();
      currentLayout = nextLayout;
      onLayoutBegin(currentLayout);
      requestAnimationFrame(tick);
    }
  };
  const deschedule = (node: RelationshipsNode, recursive: boolean) => {
    scheduled.delete(node);

    if (recursive && node.children && node.open) {
      node.children.forEach(child => deschedule(child, true));
    }
  };
  // #endregion
  const updateFocusVisuals = () => {
    const selectedNode = lastHoveredNode || lastClickedNode;

    // Handle case where lastClickedLink is set
    if (lastClickedLink && !selectedNode) {
      focusNodes = new Set([
        lastClickedLink.source,
        lastClickedLink.target,
        ...(visualStates.highlightedNodes ?? []),
        ...(visualStates.selectedNodes ?? []),
      ]);

      focusLinks = new Set([
        lastClickedLink,
        ...(visualStates.highlightedLinks ?? []),
        ...(visualStates.selectedLinks ?? []),
      ]);

      markedNodes = new Set<RelationshipsNode>([
        lastClickedLink.source,
        lastClickedLink.target,
      ]);

      markedLinks = new Set([lastClickedLink]);
      return;
    }

    // Handle case where lastClickedNode is set
    const clickRelevantNodes = selectedNode
      ? new Set(includeAllDescendants(selectedNode))
      : null;

    const clickHighlightedElements =
      clickRelevantNodes &&
      links.reduce(
        (state, link) => {
          if (clickRelevantNodes.has(link.sourceProxy)) {
            ancestorsForEach(link.targetProxy, node =>
              state.focusNodes.add(node)
            );
            state.focusLinks.add(link);
          } else if (clickRelevantNodes.has(link.targetProxy)) {
            ancestorsForEach(link.sourceProxy, node =>
              state.focusNodes.add(node)
            );
            state.focusLinks.add(link);
          }
          return state;
        },
        {
          focusNodes: new Set(clickRelevantNodes),
          focusLinks: new Set<RelationshipsLinkVisual>(),
        }
      );

    focusNodes = new Set([
      ...(selectedNode ? includeAllDescendants(selectedNode) : []),
      ...(clickHighlightedElements?.focusNodes ?? []),
      ...(visualStates.highlightedNodes ?? []),
      ...(visualStates.selectedNodes ?? []),
    ]);

    markedNodes = new Set<RelationshipsNode>([selectedNode].filter(isTruthy));

    focusLinks = new Set([
      ...(clickHighlightedElements?.focusLinks ?? []),
      ...(visualStates.highlightedLinks ?? []),
      ...(visualStates.selectedLinks ?? []),
    ]);

    markedLinks = clickHighlightedElements?.focusLinks ?? new Set();
  };
  const initialize = (args: LayoutArgs) => {
    scheduled.clear();
    // #region update layout scope variables
    canvas = args.canvas;

    applyCollapsedGroupIds(args.rootNode, args.viewSettings.collapsedGroupIds);

    let isSameSet = false;
    if (rootNode) {
      const { anyPreviousNodesFound, isSameSet: sameSet } =
        applyPreviousPositions(args.rootNode, rootNode);
      if (!anyPreviousNodesFound) {
        nextLayout = 'packLayout'; // if no previous positions were found, this is a brand-new layout.
        isGraphFullyInView = true;
      }

      isSameSet = sameSet && !ticking;
    } else {
      nextLayout = 'packLayout';
      isGraphFullyInView = true;
    }

    const nodesWithChangedOpenState = getGroupNodesWithChangedOpenState(
      args.rootNode,
      rootNode
    );

    rootNode = args.rootNode;
    originalLinks = args.links;
    viewInstanceId = args.viewInstanceId;
    commands = args.commands;
    viewStatusReporter = args.viewStatusReporter;
    // #endregion

    commands.resetLegendSelection();
    links = generateLinkVisuals();
    canvasBounds = canvas.getBoundingClientRect();
    ticking = false;
    lastScheduled = currentTimestamp();
    dead = false;
    context = canvas.getContext('2d')!;

    const {
      minDescendants: newMinDescendants,
      maxDescendants: newMaxDescendants,
    } = getMinAndMaxDescendants(rootNode);
    minDescendants = newMinDescendants;
    maxDescendants = newMaxDescendants;
    if (!isSameSet) {
      schedule(rootNode, true);
    } else if (nodesWithChangedOpenState.size) {
      fitOnNextLayoutComplete();
      nodesWithChangedOpenState.forEach(node => schedule(node, false));
    }

    lastClickedNode = rootNode
      ? (findDescendant(
          rootNode,
          ({ id }) => id === args.viewSettings.lastClickedNodeId
        ) ?? null)
      : null;
    if (lastClickedNode) {
      updateFocusVisuals();
    }

    const descendantCountRatio = maxDescendants / minDescendants;
    const radiusMax = Math.min(
      RADIUS_MAX,
      radiusOfCircle(AREA_MIN * descendantCountRatio)
    ); // don't scale largest group beyond its ratio to the smallest group
    groupNodeRadius = radiusScaler(
      [RADIUS_MIN, radiusMax],
      [minDescendants, maxDescendants]
    );
  };
  initialize(createLayoutArgs);

  const dimCanvas = canvas.cloneNode(false) as HTMLCanvasElement; // would be nice to use OffscreenCanvas here, but it's not yet supported in some browsers.
  const dimContext = dimCanvas.getContext('2d')!;

  const toggleCollapsed = (node: RelationshipsNode) => {
    updateHighlight({ highlightedLink: null, highlightedNode: null });

    const open = !node.open;

    const collapsingParentOfLastClickedNode =
      !open &&
      lastClickedNode &&
      lastClickedNode !== node &&
      ancestorsIncludes(lastClickedNode, node);
    if (collapsingParentOfLastClickedNode) {
      lastClickedNode = null;
      drawFocusVisuals();
    }
    const currentCollapsedGroupIds = viewSettings.collapsedGroupIds;
    const newCollapsedGroupIds = !rootNode
      ? []
      : open
        ? isAllCollapsed(currentCollapsedGroupIds) // user is expanding first group from collapsed-all state. re-initialize collapsedGroupIds as every group except the one that is expanding.
          ? [...includeAllDescendants(rootNode)]
              .filter(current => current.id !== node.id && isGroupNode(current))
              .map(({ id }) => id)
          : currentCollapsedGroupIds.filter(id => id !== node.id) // user is expanding a group. remove group id from collapsedGroupIds
        : [...currentCollapsedGroupIds, node.id]; // user is collapsing a group. set collapsedGroupIds to its previous value plus the new group id
    commands.setCollapsedGroupIds(newCollapsedGroupIds);
    setNodeOpen(node, open);
  };

  const clearSelection = () => {
    lastClickedNode = null;
    lastClickedLink = null;
    lastHoveredNode = null;
    visualStates.highlightedNodes = null;
    visualStates.selectedNodes = null;
    visualStates.highlightedLinks = null;
    visualStates.selectedLinks = null;
    commands.resetLegendSelection();
    drawFocusVisuals();
  };

  /* node rendering *********************************************************/

  const truncatedLabels = new Map<RelationshipsNode, string>();
  const truncatedSubLabels = new Map<RelationshipsNode, string>();

  const getLabel = (node: RelationshipsNode, marked: boolean) => {
    const { label, subLabel, r, descendantCount, open, modelId, isComponent } =
      node;
    const previousLabel = label && truncatedLabels.get(node);
    const isExpandedGroup = descendantCount && open;
    const fontSize = getNodeFontSize(node, viewTransform, scale);
    const labelHeight = fontSize / devicePixelRatio;
    const widthForIcon = isExpandedGroup ? labelHeight : 0;
    const subLabelHeight =
      isExpandedGroup && subLabel
        ? getSubLabelHeight(viewTransform, scale) / devicePixelRatio
        : 0;

    /** offset, from bottom of node circle, of a chord with the width equal to the available label width.  */
    const showExpander = shouldShowExpander(node, viewTransform.k);
    const labelY = nodeLabelY(node, showExpander, marked || node.isContext);

    const labelInfo = isViewpointMode
      ? { label, fieldLabel: null, fieldValue: null }
      : isComponent && modelId
        ? getComponentLabelParts(modelId)
        : {
            label,
            fieldLabel: null,
            fieldValue: null,
          };

    const labelOffset = labelY - node.y;
    const truncatedLabel =
      previousLabel ||
      (node.label &&
        truncateLabel(
          labelInfo,
          r,
          scale,
          labelHeight,
          widthForIcon,
          labelOffset
        ));

    if (label && !previousLabel) {
      truncatedLabels.set(node, truncatedLabel);
    }
    const previousSubLabel =
      subLabel && isExpandedGroup && truncatedSubLabels.get(node);
    const subLabelOffset = labelOffset + labelHeight;
    const truncatedSubLabel =
      previousSubLabel ||
      (subLabel &&
        isExpandedGroup &&
        truncateLabel(
          { label: subLabel, fieldLabel: null, fieldValue: null },
          r,
          scale,
          subLabelHeight,
          0,
          subLabelOffset
        ));
    if (truncatedSubLabel && !previousSubLabel) {
      truncatedSubLabels.set(node, truncatedSubLabel);
    }
    return { label: truncatedLabel, subLabel: truncatedSubLabel || null };
  };

  const nodeRender = (
    node: RelationshipsNode,
    transform: ZoomTransform,
    windowSize: [width: number, height: number],
    isHighlightDisconnectedComponents: boolean
  ) => {
    const dim = (focusNodes.size || focusLinks.size) && !focusNodes.has(node);
    const marked = markedNodes.size > 0 && markedNodes.has(node);
    const { label, subLabel } = getLabel(node, marked);
    context.globalAlpha = dim ? DIM_ALPHA : 1;
    drawNode(
      node,
      label,
      subLabel,
      transform,
      context,
      windowSize,
      scale,
      marked,
      Boolean(node.descendantCount) &&
        node === currentHighlight.highlightedNode,
      isHighlightDisconnectedComponents
    );
    context.globalAlpha = 1;
    if (!node.open) {
      return;
    }

    node.children?.forEach(child =>
      nodeRender(
        child,
        transform.translate(node.x, node.y),
        windowSize,
        isHighlightDisconnectedComponents
      )
    );
  };

  const setBranchOpen = (node: RelationshipsNode | null, value: boolean) => {
    if (node) {
      [...includeAllDescendants(node)]
        .filter(isGroupNode)
        .forEach(descendantGroup => setNodeOpen(descendantGroup, value));
    }
  };

  const setNodeOpen = (node: RelationshipsNode | null, value: boolean) => {
    if (node && node.children && node.open !== value && !node.isSynthetic) {
      node.open = !!value;

      if (node.open) {
        schedule(node, true); // node and entire descendancy needs relayouting

        /* all of the links to currently hidden descendants for which I proxy need to be reassigned */
      } else {
        schedule(node, false); // node needs relayouting
        node.children.forEach(child => deschedule(child, true)); // kill any simulations that might be happening on the children which are about to become invisible

        /* I need to become a proxy for all of my descendant's links */
      }
      regenerateLinkVisuals();
    }
  };

  const reconcileCollapsedGroupIds = (
    node: RelationshipsNode,
    collapsedGroupIds: string[]
  ) => {
    const isOpen = getGroupOpenDecision(collapsedGroupIds);

    for (const descendant of includeAllDescendants(node)) {
      if (!isGroupNode(descendant)) continue;
      setNodeOpen(descendant, isOpen(descendant.id));
    }
  };

  const setWindow = (newWindow: WindowRect | null, newScale: number) => {
    hidePopover();
    window = newWindow;

    if (newScale !== scale) {
      scale = newScale;
      truncatedLabels.clear();
      truncatedSubLabels.clear();
    }
    isGraphFullyInView =
      !rootNode || !window || isInsideLegacy(rootNode, window);
    redraw();
  };

  if (rootNode) {
    descendantsForEach(rootNode, node => (node.r = getNodeRadius(node)));
  }

  /* payload *********************************************************/

  let viewTransform = zoomIdentity
    .scale(devicePixelRatio)
    .translate(canvas.offsetWidth / 2, canvas.offsetHeight / 2);

  const updateWindow = () => {
    /* prevaricate adjusting everything else */
    canvasBounds = canvas.getBoundingClientRect();
    const l = viewTransform.invert([0, 0]);
    const r = viewTransform.invert([
      canvas.offsetWidth * devicePixelRatio,
      canvas.offsetHeight * devicePixelRatio,
    ]);
    const newWindow: WindowRect = [l[0], l[1], r[0], r[1]];
    setWindow(newWindow, 1.0 / viewTransform.k);
    dispatchAction(
      relationshipsViewUpdateWindowRect({ currentWindowRect: newWindow }),
      viewInstanceId
    );
  };

  const drawFocusVisuals = () => {
    updateFocusVisuals();
    redraw();
  };

  const dispatcher = createDispatcher();
  const regenerateLinkVisuals = dispatcher(
    () => (links = generateLinkVisuals()),
    () => 'regenerateLinkVisuals'
  );
  const redrawImmediate = (currentLinkBundling?: number) => {
    const { bundleRelationships, highlightDisconnectedComponents } =
      viewSettings;
    const linkBundling = currentLinkBundling ?? Number(bundleRelationships);

    fixCanvasDpi(canvas);
    fixCanvasDpi(dimCanvas, canvas.width, canvas.height);
    clearContext(context, canvas.width, canvas.height);
    clearContext(dimContext, dimCanvas.width, dimCanvas.height);
    if (canvas.width === 0 || canvas.height === 0) {
      return;
    }
    if (rootNode) {
      nodeRender(
        rootNode,
        viewTransform,
        [
          canvas.offsetWidth * devicePixelRatio,
          canvas.offsetHeight * devicePixelRatio,
        ],
        highlightDisconnectedComponents
      );
    }
    const highlightLinks = focusLinks.size > 0 || focusNodes.size > 0;
    if (highlightLinks) {
      links
        .filter(link => !focusLinks.has(link))
        .forEach(link =>
          drawLink(
            link,
            window,
            linkBundling,
            viewTransform,
            dimContext,
            markedLinks.has(link)
          )
        );
      context.globalAlpha = DIM_ALPHA;
      context.drawImage(dimCanvas, 0, 0);
      context.globalAlpha = 1;
      focusLinks.forEach(link =>
        drawLink(
          link,
          window,
          linkBundling,
          viewTransform,
          context,
          markedLinks.has(link)
        )
      );
    } else {
      links.forEach(link => {
        drawLink(
          link,
          window,
          linkBundling,
          viewTransform,
          context,
          markedLinks.has(link)
        );
      });
    }
  };
  const redraw = dispatcher(
    () => redrawImmediate(),
    () => true
  );
  const notifyUpdateLayout = dispatcher(
    () => dispatchAction(relationshipsViewLayoutUpdated(), viewInstanceId),
    () => 'notifyUpdateLayout'
  );
  const deferredRedraw = throttle(redraw, 100, {
    leading: false,
    trailing: true,
  });

  const fitOnNextLayoutComplete = () =>
    onFirstAction(
      layoutComplete,
      () =>
        rootNode &&
        dispatchAction(
          relationshipsViewZoomToFit({ node: rootNode }),
          viewInstanceId
        ),
      viewInstanceId
    );

  const incrementLinkBundlingAndRedraw = (
    startValue: number,
    targetLinkBundling: number
  ) => {
    const start = currentTimestamp();
    const durationMilliseconds = 500;
    let elapsed = currentTimestamp() - start;

    const incrementAndRedraw = () => {
      const currentLinkBundling =
        startValue +
        (elapsed / durationMilliseconds) *
          Math.sign(targetLinkBundling - startValue);
      requestAnimationFrame(() => {
        redrawImmediate(currentLinkBundling);
        elapsed = currentTimestamp() - start;
        if (elapsed < durationMilliseconds) {
          requestAnimationFrame(incrementAndRedraw);
        } else {
          redraw();
        }
      });
    };
    incrementAndRedraw();
  };

  const handleHighlightDisconnectedNodesChanged = (
    newHighlightDisconnectedNodes: boolean
  ) => {
    if (!rootNode) {
      return;
    }
    if (!newHighlightDisconnectedNodes) {
      redraw();
      return;
    }

    descendantsForEach(rootNode, node => {
      if (!node.children && !node.links?.length) {
        ancestorsForEach(node, ancestor => {
          if (ancestor.open) {
            return;
          }
          setNodeOpen(ancestor, true);
        });
      }
    });

    const newCollapsedGroupIds = [...includeAllDescendants(rootNode)]
      .filter(({ descendantCount, open }) => descendantCount && !open)
      .map(({ id }) => id);
    if (!isEqual(viewSettings.collapsedGroupIds, newCollapsedGroupIds)) {
      commands.setCollapsedGroupIds(newCollapsedGroupIds);
    }

    if (!scheduled.size) {
      return;
    }
    fitOnNextLayoutComplete();
  };

  const setViewSettings = (newSettings: RelationshipsViewSettings) => {
    /**
     * Chances are high that we will acceess the view settings somewhere in an impure way.
     * Therefore, it is safer to assign the new view settings before handling the changes,
     * because the redraw opertaion depends on current view settings.
     *
     * Since we are not in a reducer, we clone the primitive values on the top level, to
     * understand what has changed.
     * Keep in mind that objects aren't cloned.
     */
    const oldSettings = {
      ...viewSettings,
    };
    viewSettings = newSettings;

    if (
      rootNode &&
      newSettings.collapsedGroupIds !== oldSettings.collapsedGroupIds
    ) {
      reconcileCollapsedGroupIds(rootNode, newSettings.collapsedGroupIds);
    }

    if (newSettings.bundleRelationships !== oldSettings.bundleRelationships) {
      incrementLinkBundlingAndRedraw(
        Number(oldSettings.bundleRelationships),
        Number(newSettings.bundleRelationships)
      );
    } else if (
      newSettings.highlightDisconnectedComponents !==
      oldSettings.highlightDisconnectedComponents
    ) {
      handleHighlightDisconnectedNodesChanged(
        newSettings.highlightDisconnectedComponents
      );
    }
  };

  const setVisualStates = (props: VisualStatesData) => {
    visualStates.highlightedNodes = props.highlightedNodes;
    visualStates.selectedNodes = props.selectedNodes;
    visualStates.highlightedLinks = enrichToVisualLinks(
      props.highlightedLinks,
      links
    );
    visualStates.selectedLinks = enrichToVisualLinks(
      props.selectedLinks,
      links
    );
    drawFocusVisuals();
  };

  const subscriptions = [
    subscribeToAction(
      zoomChanged,
      ({ transform }) => {
        viewTransform = transform;
        updateWindow();
      },
      viewInstanceId
    ),
    subscribeToAction(canvasResolvedImageReady, deferredRedraw, viewInstanceId),
    subscribeToAction(svgImageReady, deferredRedraw, viewInstanceId),
  ];
  const resizeObserver = new ResizeObserver(redraw);
  resizeObserver.observe(canvas);

  const isHitRect = (
    rect: Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>,
    mouseX: number,
    mouseY: number
  ) => {
    const [scaledX, scaledY] = viewTransform.apply([mouseX, mouseY]);
    const { x, y, height, width } = rect;
    return (
      scaledY > y && scaledY < y + height && scaledX > x && scaledX < x + width
    );
  };

  const isDisconnectedChildrenCountShown = (
    node: RelationshipsNode,
    isHighlightDisconnectedComponents: boolean
  ) =>
    !node.open &&
    isHighlightDisconnectedComponents &&
    groupDisconnectedChildren([], node).length &&
    isChildCountBadgeToBeDisplayed(
      getChildCountBadgeFontSizeForNode(
        node.r * viewTransform.k,
        viewTransform,
        scale
      )
    );

  /**
   * If the disconnected children icon of the node is shown and hit, returns
   * an object with metrics needed for popover display, null otherwise.
   */
  const getDisconnectedChildrenCountIconMetricsIfHit = (
    node: RelationshipsNode,
    mouseX: number,
    mouseY: number,
    isHighlightDisconnectedComponents: boolean
  ) => {
    if (
      isDisconnectedChildrenCountShown(node, isHighlightDisconnectedComponents)
    ) {
      const { x: nodeX, y: nodeY, r: nodeR } = node;
      const [scaledNodeX, scaledNodeY] = viewTransform.apply([nodeX, nodeY]);
      const disconnectedChildrenCount = groupDisconnectedChildren(
        [],
        node
      ).length;
      const scaledNodeR = nodeR * viewTransform.k;
      const childCountBadgeFontSize = getChildCountBadgeFontSizeForNode(
        scaledNodeR,
        viewTransform,
        scale
      );
      const [badgeX, badgeY] = badgeLocation(
        scaledNodeX,
        scaledNodeY,
        scaledNodeR
      );
      const {
        iconMetrics: { iconX, iconY, iconR },
        quantityOfDisconnectedChildren,
      } = descendantCountBadgeTextAndIconMetrics(
        disconnectedChildrenCount,
        childCountBadgeFontSize,
        badgeX,
        badgeY,
        node.descendantCount,
        context,
        isHighlightDisconnectedComponents
      );
      const isIconHit = isHitRect(
        {
          x: iconX,
          y: iconY,
          width: iconR * 2,
          height: iconR * 2,
        },
        mouseX,
        mouseY
      );

      return isIconHit
        ? { iconX, iconY, iconR, quantityOfDisconnectedChildren }
        : null;
    }
    return null;
  };

  const hitContext = new HitContext();
  hitContext.lineWidth = 25;
  const findLink = (x: number, y: number, linkBundling: number) => {
    for (let ii = links.length - 1; ii >= 0; ii--) {
      const link = links[ii];
      const linkPoints = getLinkPoints(link, window, linkBundling);
      if (!linkPoints) {
        continue;
      }
      hitContext.beginPath();
      addLinkToContext(
        link,
        linkPoints,
        linkBundling,
        hitContext,
        zoomIdentity
      );
      if (hitContext.isPointInStroke(x, y)) {
        return link;
      }
    }
    return null;
  };

  const findNode = (x: number, y: number) => {
    const doit = (
      node: RelationshipsNode,
      xx: number,
      yy: number
    ): RelationshipsNode | null => {
      let ret: RelationshipsNode | null = null;

      const localX = xx - node.x;
      const localY = yy - node.y;

      const isHit =
        Math.hypot(localX, localY) < node.r ||
        (node.children?.length &&
          !node.isSynthetic &&
          Math.hypot(localX - 0, localY - node.r) < EXPANDER_RADIUS);

      if (isHit) {
        /* within this node; check the descendants */
        if (node.children && node.open) {
          for (let i = 0; i < node.children.length && !ret; ++i) {
            ret = doit(node.children[i], localX, localY);
          }
        }

        if (!ret && !node.isSynthetic) {
          /* within this node but not a descendant */
          ret = node;
        }
      }

      return ret;
    };

    return rootNode ? doit(rootNode, x, y) : null;
  };

  const locateMouse = (event: Pick<MouseEvent, 'clientX' | 'clientY'>) => {
    const mouseX = event.clientX - canvasBounds.x;
    const mouseY = event.clientY - canvasBounds.y;
    const canvasX = mouseX * devicePixelRatio;
    const canvasY = mouseY * devicePixelRatio;
    return viewTransform.invert([canvasX, canvasY]);
  };
  let currentHighlight: SetHighlightArgs = {
    highlightedLink: null,
    highlightedNode: null,
  };
  const updateHighlight = (highlight: SetHighlightArgs) => {
    if (!isHighlightEqual(highlight, currentHighlight)) {
      currentHighlight = highlight;
      dispatchAction(relationshipsViewSetHighlight(highlight), viewInstanceId);
      redraw();
    }
  };
  const hidePopover = () => setPopover(null);
  const hit = dispatcher(
    (
      x: number,
      y: number,
      linksBundling: number,
      isHighlightDisconnectedComponents: boolean
    ) => {
      const link = findLink(x, y, linksBundling);
      if (link) {
        canvas.style.cursor = 'pointer';
        updateHighlight({
          highlightedLink: link,
          highlightedNode: null,
        });

        hidePopover();

        if (link.label) {
          showPopoverAtAbsolutePosition(x, y, link.label, 0, 0);
        }

        return;
      }
      const node = findNode(x, y);
      if (node) {
        canvas.style.cursor = 'pointer';
        updateHighlight({
          highlightedLink: null,
          highlightedNode: node,
        });

        const [nodeX, nodeY] = absolutePosition(node);

        const isBrokenImageHit =
          !node.open &&
          node.isBrokenImage &&
          y > nodeY - node.r / 3 &&
          y < nodeY + node.r / 3 &&
          x > nodeX - node.r / 3 &&
          x < nodeX + node.r / 3;

        const disconnectedChildrenIconMetrics =
          getDisconnectedChildrenCountIconMetricsIfHit(
            node,
            x,
            y,
            isHighlightDisconnectedComponents
          );

        if (disconnectedChildrenIconMetrics) {
          const { iconX, iconY, iconR, quantityOfDisconnectedChildren } =
            disconnectedChildrenIconMetrics;

          const [truePopoverX, truePopoverY] = viewTransform.invert([
            iconX,
            iconY,
          ]);

          showPopoverAtAbsolutePosition(
            truePopoverX,
            truePopoverY,
            `Group contains ${quantityOfDisconnectedChildren} disconnected components`,
            iconR / viewTransform.k,
            (iconR * 2) / viewTransform.k
          );
        }

        if (isBrokenImageHit) {
          showPopoverAtAbsolutePosition(
            nodeX,
            nodeY,
            'Image cannot be displayed',
            0,
            node.r / 3
          );
        } else if (
          !disconnectedChildrenIconMetrics &&
          shouldShowPopover(
            node,
            viewTransform,
            scale,
            truncatedLabels.get(node)
          )
        ) {
          // The user is mousing over a node with a truncated label. Show the popover so the user can read the label.
          showPopoverAtAbsolutePosition(nodeX, nodeY, node.label, 0, node.r);
        } else if (!disconnectedChildrenIconMetrics) {
          hidePopover();
        }

        return;
      }
      hidePopover();
      canvas.style.cursor = '';
      updateHighlight({
        highlightedLink: null,
        highlightedNode: null,
      });
    },
    () => 'hit'
  );

  const showPopoverAtAbsolutePosition = (
    posX: number,
    posY: number,
    popoverText: string,
    xOffest: number,
    yOffset: number
  ) => {
    const popoverX = Math.max(
      0,
      Math.min(
        canvas.offsetWidth,
        viewTransform.applyX(posX + xOffest) / devicePixelRatio
      )
    );
    const popoverY = Math.max(
      0,
      Math.min(
        canvas.offsetHeight,
        viewTransform.applyY(posY + yOffset) / devicePixelRatio
      )
    );

    const { left: canvasLeft, top: canvasTop } = canvas.getBoundingClientRect();
    setPopover({
      point: { x: canvasLeft + popoverX, y: canvasTop + popoverY },
      text: popoverText,
    });
  };

  const unsetLayout = () => {
    commands.resetLegendSelection();
    clearContext(context, canvas.width, canvas.height);
    clearContext(dimContext, dimCanvas.width, dimCanvas.height);
  };
  return {
    /* return an object which allows access to the relevant parts of above */
    setNodeOpen: (node: RelationshipsNode | null, value: boolean) =>
      setNodeOpen(node ?? rootNode, value),

    setBranchOpen: (node: RelationshipsNode | null, value: boolean) =>
      setBranchOpen(node ?? rootNode, value),

    click: (
      event: React.MouseEvent,
      viewSettings: RelationshipsViewSettings
    ) => {
      commands.resetLegendSelection();

      const [x, y] = locateMouse(event);
      const node = findNode(x, y);
      commands.setGraphSelection(node?.modelId ? [node.modelId] : []);

      if (node) {
        if (isViewpointMode && node?.modelId && node?.isComponent) {
          commands.updateDetailsDrawer(node.modelId);
        }

        lastClickedLink = null;

        const [nodeX, nodeY] = absolutePosition(node);
        const expanderHit =
          node.children?.length &&
          geometry.distanceFourArgs(x, y, nodeX, nodeY + node.r) <
            EXPANDER_RADIUS;

        if (expanderHit) {
          drawFocusVisuals();
          toggleCollapsed(node);
          return;
        }

        lastClickedNode = node;
        drawFocusVisuals();
        commands.trackNodeClickOnCanvas(
          node.children?.length ? 'group' : 'component'
        );
        return;
      }

      const link = findLink(x, y, Number(viewSettings.bundleRelationships));
      if (link) {
        lastClickedNode = null;
        lastClickedLink = link;
        drawFocusVisuals();
        return;
      }

      lastClickedNode = null;
      lastClickedLink = null;
      drawFocusVisuals();
    },
    doubleClick: (
      event: React.MouseEvent,
      viewSettings: RelationshipsViewSettings
    ) => {
      const [x, y] = locateMouse(event);

      const link = findLink(x, y, Number(viewSettings.bundleRelationships));
      if (link?.modelId) {
        updateHighlight({ highlightedLink: null, highlightedNode: null });
        commands.selectReference(link.modelId);
        return;
      }

      const node = findNode(x, y);
      if (node?.modelId) {
        updateHighlight({ highlightedLink: null, highlightedNode: null });
        if (isViewpointMode && node.isComponent) {
          commands.selectComponentInViewpointMode(node.modelId);
          return;
        }
        commands.selectComponent(node.modelId);
        return;
      }
    },

    mouseMove: (
      event: React.MouseEvent,
      viewSettings: RelationshipsViewSettings
    ) => {
      const [x, y] = locateMouse(event);
      const { highlightDisconnectedComponents, bundleRelationships } =
        viewSettings;
      hit(x, y, Number(bundleRelationships), highlightDisconnectedComponents);

      const node = findNode(x, y);
      if (lastHoveredNode?.id === node?.id) {
        return;
      }
      lastHoveredNode = node;
      drawFocusVisuals();
    },
    mouseOut: () => {
      hidePopover();
      updateHighlight({ highlightedLink: null, highlightedNode: null });
    },

    contextMenu: (event: React.MouseEvent) => {
      const [x, y] = locateMouse(event);
      const link = findLink(x, y, Number(viewSettings.bundleRelationships));
      const node = !link ? findNode(x, y) : null;

      if (!link && !node) {
        return;
      }

      commands.requestContextMenu({
        event: event.nativeEvent,
        link,
        node,
        viewInstanceId,
        toggleCollapsed,
        isViewpointMode,
      });
    },

    findNode: (event: Pick<MouseEvent, 'clientX' | 'clientY'>) => {
      const [x, y] = locateMouse(event);
      return findNode(x, y);
    },
    getLastClickedNode: () => lastClickedNode,
    cleanup: () => {
      dead = true;
      reportLayoutFinished();
      clearSubscriptions(subscriptions);
      resizeObserver.disconnect();
      dimCanvas.remove();
      hidePopover();
      clearFontMetricsCache();
    },
    redrawImmediate,
    getViewTransform: () => viewTransform,
    unsetLayout,
    initialize,
    optimizeLayout: () => {
      if (!rootNode) {
        return;
      }
      ticking = false;
      nextLayout = 'packLayout';
      fitOnNextLayoutComplete();
      schedule(rootNode, true);
    },
    isOptimizeLayoutAvailable: () => currentLayout === 'forceLayout',
    setViewSettings,
    setVisualStates,
    clearSelection,
  };
};
